import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, concatMap, Observable, shareReplay, tap} from 'rxjs';
import {map} from 'rxjs/operators';

import {environment} from '@environments/environment';
import {User, UserType} from '@app/models';
import {CookiesService} from "@app/services/cookie.service";
import {result} from "lodash-es";
import jwt_decode from "jwt-decode";
import * as moment from 'moment';

import {TranslateService} from "@ngx-translate/core";

@Injectable({providedIn: 'root'})
export class AccountService {
  private userSubject: BehaviorSubject<User | null>;
  public user: Observable<User | null>;
  private userJson: User;

  constructor(
    private router: Router,
    private http: HttpClient,
    private cookieService: CookiesService,
  ) {
    this.userSubject = new BehaviorSubject<User | null>(JSON.parse(<string>localStorage.getItem('user')));
    this.user = this.userSubject.asObservable();
  }

  public get userValue(): User | null {
    return this.userSubject.value;
  }
  public get language(): String {
    if (this.userValue?.language){
      return this.userValue?.language
    }
    return environment.LANGUAGE_DEFAULT
  }

  get token(): string | undefined {
    return this.userValue?.access
  }

  private setSession(authResult: any) {
    // this.userJson = authResult;
    const token = authResult.access;
    const payload: any = jwt_decode(token);
    const expiresAt = moment.unix(payload.exp).toISOString();
    // this.userJson.expires_at = expiresAt;
    this.userSubject.next({...authResult, expires_at: expiresAt})
    localStorage.setItem('user', JSON.stringify(this.userSubject.value));
  }

  private setToken(authResult: any) {
    this.userSubject.next(<User>{...this.userSubject.value, access: authResult.access})
    localStorage.setItem('user', JSON.stringify(this.userSubject.value));

    if (this.token) {
      this.cookieService.setCookie(environment.DIY_COOKIE_NAME, this.token, 30, '/', environment.DIY_COOKIE_DOMAIN)
    }

  }

  login(email: any, password: any) {
    return this.http.post<User>(`${environment.api.auth}/${this.language}/api/v1/token/`, {username: email, password})
      .pipe(
        concatMap((user_auth) => {
          this.setSession(user_auth);
          return this.profile()
        }),
        map((profile) => {
          console.log("AccounService->profile", profile)

          return this.userJson
        })
      );
  }

  profile() {
    return this.http.get<User>(`${environment.api.auth}/${this.language}/api/v1/accounts/profile/`, {})
      .pipe(map(profile => {
        const user = {...this.userSubject.value, ...profile}
        console.log('profile', user);
        this.cookieService.setCookie(environment.DIY_COOKIE_NAME, user.access, 30, '/', environment.DIY_COOKIE_DOMAIN)
        this.userSubject.next(user);
        if(user.organization){
          this.cookieService.setCookie(environment.DIY_COOKIE_TENANT, user.organization.id, 30, '/', environment.DIY_COOKIE_DOMAIN)
        }
        localStorage.setItem('user', JSON.stringify(user));
        return user;
      }));
  }

  refreshToken() {
    if (moment().isBetween(this.getExpiration().subtract(1, 'hours'), this.getExpiration())) {
      console.log('refreshToken', this.getExpiration())
      return this.http.post(`${environment.api.auth}/${this.language}/api/v1/token/refresh/`,
        {refresh: this.userValue?.refresh}
      ).pipe(
        tap(response => this.setToken(response)),
        shareReplay(),
      ).subscribe();
    }
    return;
  }


  logout() {
    // remove user from local storage and set current user to null
    localStorage.removeItem('user');
    this.userSubject.next(null);
    this.cookieService.deleteCookie(environment.DIY_COOKIE_NAME);
    this.cookieService.deleteCookie(environment.DIY_COOKIE_TENANT);
    this.router.navigateByUrl('/login');
  }

  getExpiration() {
    return moment(this.userValue?.expires_at);
  }

  isLoggedIn() {
    return moment().isBefore(this.getExpiration());
  }

  isLoggedOut() {
    return !this.isLoggedIn();
  }

  forgetPassword(email: string){
    return this.http.post<any>(`${environment.api.auth}/${this.language}/api/v1/password-reset/`, { email })
  }
  resetPassword(password: string, token: string){
    return this.http.post<any>(`${environment.api.auth}/${this.language}/api/v1/password-reset/confirm/`, { password, token})
  }

  connectToOrganization(org: any){
    const user: any = { ...this.userSubject.value, connectedToOrg: org}
    console.log("connectToOrganization", user)
    this.userSubject.next(user);
    localStorage.setItem('user', JSON.stringify(user));
  }

  has_access(module: string){

    const user = this.userValue;
    if(!user){
      return false
    }

    if(user?.user_type === UserType.DoItYourself || user?.user_type === UserType.Merchandising){
      return true;
    }
    
    if(user?.organization?.ms_access && user.organization.ms_access.hasOwnProperty(module)){
      return user.organization.ms_access[module];
    }else{
      return false;
    }
  }

  mercha_with_org(){
    const user = this.userValue;
    return user?.user_type === UserType.Merchandising && user?.connectedToOrg
  }

  currentOrgIsMerchandising(): boolean {
    const user = this.userValue;
    if (!user) {
      return false;
    }
    const org: any = user.connectedToOrg || user.organization;
    return !!org?.enabled_merchandising;
  }
}
